import { Container } from 'pixi.js';
import { Signal } from 'typed-signals';
import type { Tournament } from '../../app/tournaments/types';
import { TournamentPanelView } from './TournamentPanelView';
import type { PlatformUser, TournamentData } from '../../app/platform/Platform';
import { TournamentCreatePanelLayout } from '../../../layouts/mainMenu/tournament/TournamentCreatePanelLayout';

export enum TournamentBlankPageType {
  Empty = 'Empty',
  CreateTournament = 'CreateTournament',
}

interface TournamentTabViewConfig {
  currentUser: PlatformUser;
  activeTournament: TournamentData | null;
  tournamentPromise: Promise<Tournament[]>;
  blankPageType: TournamentBlankPageType;
}

export class TournamentTabView extends Container {
  private readonly panelSpacing = {
    horizontal: {
      marginY: 100,
      spacing: 700,
    },
    vertical: {
      marginY: 50,
      spacing: 800,
    },
  };

  public readonly onTournamentJoinClicked = new Signal<(tournamentId: string) => void>();

  public readonly onTournamentCreateClicked = new Signal();

  public readonly onViewReady = new Signal();

  constructor(private readonly config: TournamentTabViewConfig) {
    super();

    this.config.tournamentPromise.then(
      (tournaments) => {
        this.init(tournaments);
      },
      () => {},
    );
  }

  private init(tournaments: Tournament[]): void {
    if (this.destroyed) return;

    const { currentUser, activeTournament } = this.config;

    if (tournaments.length === 0) {
      this.initBlankPage();
    } else {
      tournaments.forEach((tournamentData) => {
        const view = new TournamentPanelView({
          currentUser,
          tournament: tournamentData,
          isTournamentActive: activeTournament?.tournamentId === tournamentData.tournamentId,
        }).props({
          parent: this,
        });
        view.onJoinClicked.connect(() => {
          this.onTournamentJoinClicked.emit(tournamentData.tournamentId);
        });
      });
    }

    this.setHorizontalLayout(false);

    this.onViewReady.emit();
  }

  private initBlankPage(): void {
    if (this.config.blankPageType === TournamentBlankPageType.Empty) return;

    const { root, createButton } = new TournamentCreatePanelLayout();

    root.props({
      parent: this,
      y: 50,
    });

    createButton.on('pointertap', () => {
      this.onTournamentCreateClicked.emit();
    });
  }

  public setHorizontalLayout(value: boolean): void {
    this.children?.forEach((child, index) => {
      const { spacing, marginY } = this.panelSpacing[value ? 'horizontal' : 'vertical'];

      const positionValue = spacing * index;

      child.props({
        x: value ? positionValue : 0,
        y: marginY + (value ? 0 : positionValue),
      });
    });
  }
}
